import React, { Component } from 'react';
import { array, arrayOf, bool, func, object, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import classNames from 'classnames';
import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { Field, Form as FinalForm } from 'react-final-form';
import * as validators from '../../util/validators';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
  resolveDefaultSortKey,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { propTypes } from '../../util/types';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { hasKeywordSearchParams, hasLocationSearchParams } from '../../util/searchPageQueryParams';
import { userLocation } from '../../util/maps';
import { types as sdkTypes } from '../../util/sdkLoader';

import {
  Button,
  H3,
  H5,
  Heading,
  Icons,
  Modal,
  ModalInMobile,
  Page,
  FieldTextInput,
  FieldCheckbox,
  Form,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { fetchListings, setActiveListing } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SearchLoadingIndicator from './SearchLoadingIndicator/SearchLoadingIndicator';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import AccessFilter from './AccessFilter/AccessFilter';

import css from './SearchPage.module.css';
import SectionTopbarFilter from './SectionTopbarFilter/SectionTopbarFilter';
import { resolveSearchMapBounds } from './SearchMapBounds';
import {
  buildCanonicalMapSearchParams,
  buildPublicMooragesToggleSearchParams,
  getSynchronizedListings,
  hasSameSearchQueryParams,
  shouldFetchExtendedBoundsAfterSearch,
} from './SearchPageWithMap.helpers';
import {
  clearSearchLocationState,
  loadSearchLocationState,
  saveSearchLocationState,
} from './SearchStatePersistence';
import { withViewport } from '../../util/uiHelpers';
import { extendBoundsByMiles, isArrayLength } from '../../util/genericHelpers';
import { submitOnBuoyCrmRecord } from '../../ducks/global.duck';
import { onTriggerError, onTriggerSuccess } from '../../util/toast';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { getMarketingEventReminder } from '../../util/dataExtractors';
import { SearchType } from '../../util/enums';
import { DEFAULT_MILE } from '../../util/constants';
import { hasSeenMarketingReminder, markMarketingReminderAsSeen } from '../../util/helperFunctions';
import { isMobileViewport } from '../../util/viewport';
import { normalizeAccessFilter, PUBLIC_MOORAGES_QUERY_PARAM } from '../../util/publicMoorages';
import { ACCESS_FILTER_ALL, ACCESS_FILTER_QUERY_PARAM } from '../../util/publicMoorageConstants';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 450; // Wait for map interactions to settle before syncing search.

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;
const VIEW_MODE_MAP_SPLIT = 'mapSplit';
const VIEW_MODE_GRID = 'grid';
const GEOLOCATION_RESTORE_TIMEOUT_MS = 1200;
const { LatLng, LatLngBounds } = sdkTypes;

const parseLocationSearchParams = search =>
  parse(search || '', {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

const SEARCH_BOOTSTRAP_IGNORE_QUERY_KEYS = [
  'page',
  'mapSearch',
  'sort',
  'mobilemenu',
  'mobilesearch',
  'address',
  'search',
  'bounds',
  'origin',
  'placeId',
];

const isFiniteNumber = value => Number.isFinite(value);

const isValidLatLng = value =>
  !!value &&
  isFiniteNumber(value.lat) &&
  isFiniteNumber(value.lng) &&
  value.lat >= -90 &&
  value.lat <= 90 &&
  value.lng >= -180 &&
  value.lng <= 180;

const isValidBounds = value => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return isValidLatLng(value.ne) && isValidLatLng(value.sw);
};

const centerFromBounds = bounds => {
  if (!isValidBounds(bounds)) {
    return null;
  }

  return new LatLng((bounds.ne.lat + bounds.sw.lat) / 2, (bounds.ne.lng + bounds.sw.lng) / 2);
};

const trimString = value => (typeof value === 'string' ? value.trim() : '');

const getCurrentPageUrl = () =>
  typeof window !== 'undefined' && window.location ? window.location.href : '';

const getSearchContext = location => {
  const parsedSearch = parseLocationSearchParams(location?.search);
  const { address, search, keywords, keyword, q, origin, bounds } = parsedSearch || {};
  const center = isValidLatLng(origin) ? origin : centerFromBounds(bounds);
  const searchedLocation = trimString(address) || trimString(search) || trimString(keywords);
  const searchQuery =
    trimString(keywords) || trimString(keyword) || trimString(q) || searchedLocation;

  return {
    searchedLocation,
    searchQuery,
    searchedLat: isFiniteNumber(center?.lat) ? String(center.lat) : null,
    searchedLng: isFiniteNumber(center?.lng) ? String(center.lng) : null,
  };
};

const getCurrentUserContactFields = currentUser => {
  const profile = currentUser?.attributes?.profile || {};
  const protectedData = profile.protectedData || {};
  const privateData = profile.privateData || {};

  return {
    firstName: trimString(profile.firstName),
    lastName: trimString(profile.lastName),
    phone: trimString(
      protectedData.phoneNumber ||
        privateData.phoneNumber ||
        protectedData.phone ||
        privateData.phone
    ),
  };
};

const buildNewMarketMessage = ({ searchedLocation, homePort, pageUrl, wantsPartnerInfo }) => {
  return [
    'New market request submitted from OnBuoy search.',
    `Search/location: ${searchedLocation || 'Unknown'}`,
    homePort ? `User message: Home port / cruising area: ${homePort}` : null,
    `Local partner interest: ${wantsPartnerInfo ? 'yes' : 'no'}`,
    pageUrl ? `Page URL: ${pageUrl}` : null,
  ]
    .filter(Boolean)
    .join('\n\n');
};

const buildNewMarketCrmPayload = ({ values, currentUser, location }) => {
  const email = trimString(values?.email);
  const homePort = trimString(values?.homePort);
  const { firstName, lastName, phone } = getCurrentUserContactFields(currentUser);
  const { searchedLocation, searchQuery, searchedLat, searchedLng } = getSearchContext(location);
  const pageUrl = getCurrentPageUrl();
  const wantsPartnerInfo = !!values?.isCheckboxChecked;

  return {
    record_source: 'website_contact_modal',
    first_name: firstName,
    last_name: lastName,
    email,
    phone,
    message: buildNewMarketMessage({
      searchedLocation: searchedLocation || searchQuery,
      homePort,
      pageUrl,
      wantsPartnerInfo,
    }),
    company_website: '',
    source_page: 'new_market_modal',
    local_partner_interest: wantsPartnerInfo,
    ...(homePort ? { home_port: homePort } : {}),
    ...(searchedLocation ? { searched_location: searchedLocation } : {}),
    ...(searchedLat ? { searched_lat: searchedLat } : {}),
    ...(searchedLng ? { searched_lng: searchedLng } : {}),
    ...(searchQuery ? { search_query: searchQuery } : {}),
    ...(pageUrl ? { page_url: pageUrl } : {}),
  };
};

const boundsAroundCenter = (center, distanceMeters = 80000) => {
  if (!isValidLatLng(center)) {
    return null;
  }

  const latDelta = Math.max(distanceMeters / 111000, 0.03);
  const cosLat = Math.cos((center.lat * Math.PI) / 180);
  const lngDelta = Math.max(distanceMeters / (111000 * Math.max(Math.abs(cosLat), 0.15)), 0.03);
  const ne = new LatLng(Math.min(center.lat + latDelta, 85), Math.min(center.lng + lngDelta, 180));
  const sw = new LatLng(
    Math.max(center.lat - latDelta, -85),
    Math.max(center.lng - lngDelta, -180)
  );

  return new LatLngBounds(ne, sw);
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSearchMapOpenOnMobile: false,
      isMobileModalOpen: false,
      viewMode: VIEW_MODE_MAP_SPLIT,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      notifyModalOpen: false,
      userClosedModal: false,
      currentSearchCycle: Date.now(),
      hasExtendedBoundsFetched: false,
    };

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
    this.handleAccessFilterChange = this.handleAccessFilterChange.bind(this);
    this.onToggleMap = this.onToggleMap.bind(this);

    this.openNotifyModal = this.openNotifyModal.bind(this);
    this.closeNotifyModal = this.closeNotifyModal.bind(this);
    this.handleNotifySubmit = this.handleNotifySubmit.bind(this);
    this.fetchListingsWithExtendedBounds = this.fetchListingsWithExtendedBounds.bind(this);
    this.handlePublicMoorageCheckInSuccess = this.handlePublicMoorageCheckInSuccess.bind(this);
    this.updateSearchURL = this.updateSearchURL.bind(this);
    this.initializeSearchLocationState = this.initializeSearchLocationState.bind(this);
    this.persistLocationStateFromSearch = this.persistLocationStateFromSearch.bind(this);
    this.applyLocationStateToSearch = this.applyLocationStateToSearch.bind(this);
    this.resolveGeolocationStateIfGranted = this.resolveGeolocationStateIfGranted.bind(this);
    this.lastUrlSyncSource = null;
    this.pendingPersistedSource = null;
    this.hasUnmounted = false;
    this.suppressExtendedBoundsAfterToggle = false;
  }

  getSearchFilterConfigs(locationSearch = this.props.location.search) {
    const { config } = this.props;
    const { listingFields: listingFieldsConfigRaw } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};
    const categoryConfiguration = config?.categoryConfiguration || {};
    const listingCategories = categoryConfiguration.categories || [];
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields: listingFieldsConfigRaw,
      locationSearch,
      categoryConfiguration,
    });

    return {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };
  }

  hasUsableLocationContext(search) {
    const parsed = parseLocationSearchParams(search);
    const hasAddress =
      typeof parsed?.address === 'string'
        ? parsed.address.trim().length > 0
        : typeof parsed?.search === 'string'
        ? parsed.search.trim().length > 0
        : false;
    return isValidBounds(parsed?.bounds) || isValidLatLng(parsed?.origin) || hasAddress;
  }

  hasExplicitSearchCriteria(search) {
    const parsed = parseLocationSearchParams(search);
    const explicitParams = Object.entries(parsed || {}).reduce((picked, [key, value]) => {
      if (SEARCH_BOOTSTRAP_IGNORE_QUERY_KEYS.includes(key)) {
        return picked;
      }

      return value == null ? picked : { ...picked, [key]: value };
    }, {});
    const filterConfigs = this.getSearchFilterConfigs(search);
    const validExplicitParams = validFilterParams(explicitParams, filterConfigs, false);

    return Object.keys(validExplicitParams).length > 0;
  }

  getLocationSource({ parsedSearch, sourceHint }) {
    if (sourceHint && ['geolocation', 'manual', 'searchBox', 'unknown'].includes(sourceHint)) {
      return sourceHint;
    }

    if (sourceHint === 'map') {
      return 'manual';
    }

    if (parsedSearch?.placeId) {
      return 'searchBox';
    }

    if (parsedSearch?.mapSearch) {
      return 'manual';
    }

    return 'unknown';
  }

  persistLocationStateFromSearch(search, sourceHint) {
    const parsedSearch = parseLocationSearchParams(search);
    const bounds = isValidBounds(parsedSearch?.bounds) ? parsedSearch.bounds : null;
    const centerFromOrigin = isValidLatLng(parsedSearch?.origin)
      ? new LatLng(parsedSearch.origin.lat, parsedSearch.origin.lng)
      : null;
    const center = centerFromOrigin || centerFromBounds(bounds);

    if (!center && !bounds) {
      return false;
    }

    const addressRaw =
      typeof parsedSearch?.address === 'string'
        ? parsedSearch.address
        : typeof parsedSearch?.search === 'string'
        ? parsedSearch.search
        : '';
    const address = addressRaw.trim();

    saveSearchLocationState({
      center,
      bounds,
      source: this.getLocationSource({ parsedSearch, sourceHint }),
      ...(address ? { address } : {}),
      updatedAt: Date.now(),
    });

    return true;
  }

  buildSearchParamsFromLocationState(locationState) {
    const { location, config } = this.props;
    const filterConfigs = this.getSearchFilterConfigs(location.search);
    const storedCenter = isValidLatLng(locationState?.center)
      ? new LatLng(locationState.center.lat, locationState.center.lng)
      : null;
    const storedBounds = isValidBounds(locationState?.bounds)
      ? new LatLngBounds(
          new LatLng(locationState.bounds.ne.lat, locationState.bounds.ne.lng),
          new LatLng(locationState.bounds.sw.lat, locationState.bounds.sw.lng)
        )
      : null;
    const viewportCenter = storedCenter || centerFromBounds(storedBounds);
    const viewportBounds =
      storedBounds ||
      boundsAroundCenter(viewportCenter, config?.maps?.search?.currentLocationBoundsDistance);

    if (!viewportCenter || !viewportBounds) {
      return null;
    }

    const searchParams = buildCanonicalMapSearchParams({
      locationSearch: location.search,
      config,
      filterConfigs,
      viewportBounds,
      viewportCenter,
    });

    const address = typeof locationState?.address === 'string' ? locationState.address.trim() : '';
    if (address) {
      return {
        ...searchParams,
        address,
        search: address,
      };
    }

    return searchParams;
  }

  applyLocationStateToSearch(locationState, options = {}) {
    const { source = 'restore' } = options;
    const searchParams = this.buildSearchParamsFromLocationState(locationState);

    if (!searchParams) {
      return false;
    }

    this.pendingPersistedSource =
      this.getLocationSource({ parsedSearch: null, sourceHint: locationState?.source }) ||
      'unknown';
    this.updateSearchURL(searchParams, { replace: true, source });
    return true;
  }

  async resolveGeolocationStateIfGranted() {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
      return null;
    }

    try {
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
      if (permissionStatus?.state !== 'granted') {
        return null;
      }
    } catch (error) {
      return null;
    }

    try {
      let timeoutId = null;
      const timeoutPromise = new Promise(resolve => {
        timeoutId = setTimeout(() => resolve(null), GEOLOCATION_RESTORE_TIMEOUT_MS);
      });

      const currentLocation = await Promise.race([
        userLocation().catch(() => null),
        timeoutPromise,
      ]);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (!currentLocation || this.hasUnmounted) {
        return null;
      }

      const bounds = boundsAroundCenter(
        currentLocation,
        this.props.config?.maps?.search?.currentLocationBoundsDistance
      );

      if (!bounds) {
        return null;
      }

      return {
        center: currentLocation,
        bounds,
        source: 'geolocation',
        updatedAt: Date.now(),
      };
    } catch (error) {
      return null;
    }
  }

  async initializeSearchLocationState() {
    const currentSearch = this.props.location?.search || '';

    if (this.hasUsableLocationContext(currentSearch)) {
      this.persistLocationStateFromSearch(currentSearch, 'unknown');
      this.ensureDefaultSortInURL();
      return;
    }

    if (this.hasExplicitSearchCriteria(currentSearch)) {
      // Explicit deep links like homepage category links or keyword searches
      // should keep their URL-driven search instead of being rewritten to a
      // restored location search on first mount.
      this.ensureDefaultSortInURL();
      return;
    }

    const restoredState = loadSearchLocationState();
    if (restoredState) {
      const didApplyRestoredState = this.applyLocationStateToSearch(restoredState, {
        source: 'restore',
      });
      if (didApplyRestoredState) {
        return;
      }
      clearSearchLocationState();
    }

    const geolocationState = await this.resolveGeolocationStateIfGranted();
    if (this.hasUnmounted) {
      return;
    }

    if (geolocationState) {
      saveSearchLocationState(geolocationState);
      const didApplyGeolocationState = this.applyLocationStateToSearch(geolocationState, {
        source: 'restore',
      });
      if (didApplyGeolocationState) {
        return;
      }
    }

    // Configured default locations are autocomplete suggestions, not
    // an implicit fallback search target for a bare `/s` load.
    this.ensureDefaultSortInURL();
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
    this.initializeSearchLocationState();
  }

  ensureDefaultSortInURL() {
    const { location, config } = this.props;
    const sortConfig = config?.search?.sortConfig;
    const sortParamName = sortConfig?.queryParamName || 'sort';
    const params = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    if (params?.[sortParamName]) {
      return;
    }

    const defaultSort = resolveDefaultSortKey(sortConfig, params);
    if (!defaultSort) {
      return;
    }

    this.updateSearchURL(
      {
        ...params,
        [sortParamName]: defaultSort,
      },
      { replace: true, source: 'sortSync' }
    );
  }

  updateSearchURL(searchParams, options = {}) {
    const { replace = false, source = null } = options;
    const { history, routeConfiguration, location } = this.props;
    const currentSearchParams = parseLocationSearchParams(location.search);

    if (hasSameSearchQueryParams(currentSearchParams, searchParams)) {
      return false;
    }

    this.lastUrlSyncSource = source;
    const resource = createResourceLocatorString(
      'SearchPage',
      routeConfiguration,
      {},
      searchParams
    );
    if (replace) {
      history.replace(resource);
      return true;
    }
    history.push(resource);
    return true;
  }

  fetchListingsWithExtendedBounds(extendedBounds) {
    const { history, location, config, routeConfiguration } = this.props;

    const parsed = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const { bounds: oldBounds, mapSearch, ...otherParams } = parsed;

    const baseParams = {
      ...otherParams,
      ...(isOriginInUse(config) && parsed.origin ? { origin: parsed.origin } : {}),
    };

    const searchParams = {
      ...baseParams,
      bounds: extendedBounds,
      mapSearch: true,
    };

    this.setState(
      {
        hasExtendedBoundsFetched: true,
        notifyModalOpen: false,
        userClosedModal: false,
      },
      () => {
        history.replace(
          createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams)
        );
      }
    );
  }

  handlePublicMoorageCheckInSuccess(result) {
    const { location, onActivateListing, onFetchListings, config } = this.props;
    const listingId = result?.checkIn?.listingId;
    if (listingId) {
      onActivateListing({ uuid: listingId });
    }
    return onFetchListings?.({ search: location.search, config, force: true });
  }

  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportMapCenter, viewportMapZoom } = data;

    const routes = this.props.routeConfiguration;
    const searchPagePath = pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)

    if (viewportBoundsChanged && isSearchPage) {
      const { location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
      };

      const searchParams = buildCanonicalMapSearchParams({
        locationSearch: location.search,
        config: this.props.config,
        filterConfigs,
        viewportBounds,
        viewportCenter: viewportMapCenter,
      });
      const currentSearchParams = parseLocationSearchParams(location.search);
      if (hasSameSearchQueryParams(currentSearchParams, searchParams)) {
        return;
      }
      const parsedSearch = parseLocationSearchParams(location.search);
      const addressRaw =
        typeof parsedSearch?.address === 'string'
          ? parsedSearch.address
          : typeof parsedSearch?.search === 'string'
          ? parsedSearch.search
          : '';
      const address = addressRaw.trim();

      saveSearchLocationState({
        center: viewportMapCenter,
        bounds: viewportBounds,
        ...(isFiniteNumber(viewportMapZoom) ? { zoom: viewportMapZoom } : {}),
        source: 'manual',
        ...(address ? { address } : {}),
        updatedAt: Date.now(),
      });

      const searchCycleId = Date.now();
      // Reset state for a new search cycle
      this.setState({
        currentSearchCycle: searchCycleId,
        notifyModalOpen: false,
        hasExtendedBoundsFetched: false, // Reset for new search
      });
      this.updateSearchURL(searchParams, { replace: true, source: 'map' });
    }
  }

  openNotifyModal() {
    this.setState({ notifyModalOpen: true, userClosedModal: false });
  }

  closeNotifyModal() {
    markMarketingReminderAsSeen();
    this.setState({ notifyModalOpen: false, userClosedModal: true });
  }

  handleNotifySubmit = values => {
    const { onSubmitOnBuoyCrmRecord, onUpdateProfile, currentUser, location } = this.props;

    if (values.email) {
      if (trimString(values?.company_website)) {
        this.closeNotifyModal();
        return Promise.resolve();
      }

      const payload = buildNewMarketCrmPayload({ values, currentUser, location });

      return onSubmitOnBuoyCrmRecord(payload)
        .then(response => {
          if (response?.success === true) {
            onTriggerSuccess('Notify me request successfully sent');
            this.closeNotifyModal();

            if (currentUser?.id) {
              onUpdateProfile({
                protectedData: { marketingEventReminder: true },
              });
            } else {
              markMarketingReminderAsSeen();
            }
          } else {
            const errorMessage = response?.message || 'Failed to send request';
            if (process.env.NODE_ENV === 'development') {
              console.warn('OnBuoy CRM ingest rejected notify modal submission', {
                id: response?.id,
                status: response?.status,
                message: response?.message,
              });
            }
            onTriggerError(errorMessage);
          }
        })
        .catch(error => {
          const errorMessage =
            error?.data?.message ||
            error?.response?.data?.message ||
            'Something went wrong. Please try again.';
          if (process.env.NODE_ENV === 'development') {
            console.warn('OnBuoy CRM ingest request failed', {
              status: error?.status,
              message: error?.message,
            });
          }
          onTriggerError(errorMessage);
        });
    }

    return Promise.resolve();
  };

  componentDidUpdate(prevProps) {
    const { searchInProgress, listings, location, currentUser } = this.props;
    const searchJustSettled = prevProps.searchInProgress && !searchInProgress;
    const locationChanged = prevProps.location.search !== location.search;
    if (locationChanged) {
      const urlSyncSource = this.lastUrlSyncSource;
      const sourceHintFromURLSync =
        this.pendingPersistedSource ||
        (urlSyncSource === 'map' ? 'manual' : urlSyncSource === 'restore' ? 'unknown' : null);
      this.persistLocationStateFromSearch(location.search, sourceHintFromURLSync);
      this.pendingPersistedSource = null;
      const skipDefaultSortSync = urlSyncSource === 'map';
      this.lastUrlSyncSource = null;
      if (!skipDefaultSortSync) {
        this.ensureDefaultSortInURL();
      }
    }
    const marketingEventReminder =
      getMarketingEventReminder(currentUser) || hasSeenMarketingReminder();

    const { searchParamsInURL } = searchParamsPicker(
      location.search,
      this.props.searchParams,
      {
        listingFieldsConfig: this.props.config?.listing?.listingFields,
        defaultFiltersConfig: this.props.config?.search?.defaultFilters,
        listingCategories: this.props.config.categoryConfiguration.categories,
      },
      this.props.config?.search?.sortConfig,
      isOriginInUse(this.props.config)
    );

    const { bounds } = searchParamsInURL || {};
    const { mapSearch = false } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const keywordSearchActive = hasKeywordSearchParams(searchParamsInURL);
    const locationSearchActive = hasLocationSearchParams(searchParamsInURL);
    const searchType =
      keywordSearchActive && !locationSearchActive
        ? SearchType.KEYWORDS
        : location?.state?.searchType || SearchType.LOCATION;
    if (
      !searchInProgress &&
      !marketingEventReminder &&
      this.state.currentSearchCycle &&
      searchType === SearchType.LOCATION
    ) {
      // Initial fetch completed with 0 listings
      const shouldFetchExtendedBounds = shouldFetchExtendedBoundsAfterSearch({
        listings,
        previousSearchInProgress: prevProps.searchInProgress,
        searchInProgress,
        hasExtendedBoundsFetched: this.state.hasExtendedBoundsFetched,
        hasMarketingReminder: marketingEventReminder,
        searchType,
        isMapSearchActive: mapSearch,
        suppressAutoExpand: this.suppressExtendedBoundsAfterToggle,
      });
      if (shouldFetchExtendedBounds) {
        const extendedBounds = bounds ? extendBoundsByMiles(bounds, DEFAULT_MILE) : null;
        if (extendedBounds) {
          this.fetchListingsWithExtendedBounds(extendedBounds, this.state.currentSearchCycle);
        }
      }

      // Check after any fetch (initial or extended) if listings are still 0
      if (
        !isArrayLength(this.props.listings) && // listings.length === 0
        prevProps.searchInProgress && // Fetch just completed
        !this.state.notifyModalOpen && // Modal isn’t already open
        this.state.hasExtendedBoundsFetched
      ) {
        this.openNotifyModal();
      }
    }

    if (searchJustSettled) {
      this.suppressExtendedBoundsAfterToggle = false;
    }
  }

  componentWillUnmount() {
    this.hasUnmounted = true;
    this.onMapMoveEnd.cancel();
    this.setState({
      currentSearchCycle: null,
      hasExtendedBoundsFetched: false,
    });
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  onToggleMap() {
    this.setState(prevState => ({
      viewMode: prevState.viewMode === VIEW_MODE_MAP_SPLIT ? VIEW_MODE_GRID : VIEW_MODE_MAP_SPLIT,
    }));
  }

  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    const queryParams = omit(urlQueryParams, [
      ...filterQueryParamNames,
      ACCESS_FILTER_QUERY_PARAM,
      PUBLIC_MOORAGES_QUERY_PARAM,
    ]);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);
          history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  handleAccessFilterChange(access) {
    const { location, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };
    const queryParams = buildPublicMooragesToggleSearchParams({
      locationSearch: location.search,
      filterConfigs,
      access,
    });

    // Keep toggle-driven empty states stable instead of treating them like
    // location searches that should auto-expand the map bounds.
    this.suppressExtendedBoundsAfterToggle = true;
    this.updateSearchURL(queryParams, { source: 'filter' });
  }

  render() {
    const {
      intl,
      listings,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      activeListingId,
      onActivateListing,
      routeConfiguration,
      onFetchListings,
      config,
      viewport,
      history,
      addContactLoading,
      currentUser,
    } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const { notifyModalOpen, viewMode } = this.state;
    const isGridView = viewMode === VIEW_MODE_GRID;

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );

    const validQueryParams = urlQueryParams;

    const isMobileLayout = isMobileViewport(viewport);
    const shouldShowSearchMap =
      !isMobileLayout || (isMobileLayout && this.state.isSearchMapOpenOnMobile);

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const extraSliderFilter = [
      {
        key: 'pub_maxBoatSize',
        schemaType: 'pub_maxBoatSize',
        label: 'Boat size (ft)',
        min: 1,
        max: 200,
        step: 1,
      },
      {
        key: 'pub_waterDepth',
        schemaType: 'pub_waterDepth',
        label: 'Water Depth (ft)',
        min: 1,
        max: 200,
        step: 1,
      },
    ];
    const availablePrimaryFilters = [
      ...customPrimaryFilters,
      ...defaultFiltersConfig,
      ...extraSliderFilter,
    ];

    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
      ...extraSliderFilter,
    ];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const selectedFilterParams = validFilterParams(validQueryParams, filterConfigs);
    const keysOfSelectedFilters = Object.keys(selectedFilterParams);
    const accessFilterValue = normalizeAccessFilter(validQueryParams[ACCESS_FILTER_QUERY_PARAM]);
    const selectedFiltersCountForMobileBase = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;
    const selectedFiltersCountForMobile =
      selectedFiltersCountForMobileBase + (accessFilterValue !== ACCESS_FILTER_ALL ? 1 : 0);
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

    // Selected aka active secondary filters
    const selectedSecondaryFilters = hasSecondaryFilters
      ? validFilterParams(validQueryParams, {
          listingFieldsConfig: customSecondaryFilters,
          defaultFiltersConfig: [],
          listingCategories,
        })
      : {};
    const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
          },
          selectedSecondaryFiltersCount,
        }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);
    const synchronizedListings = getSynchronizedListings({
      listings,
      listingsAreLoaded,
    });

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          {...(mode === 'desktop' ? { className: css.toolbarFilterControl } : {})}
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { origin } = searchParamsInURL || {};
    const boundsForSearchMap = resolveSearchMapBounds({
      searchParamsInURL,
      listings: synchronizedListings,
    });
    const { title, description, schema } = createSearchResultSchema(
      synchronizedListings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;
    const desktopAccessFilter = (
      <AccessFilter
        id="SearchPageWithMap.accessFilter"
        className={css.toolbarFilterControl}
        value={accessFilterValue}
        onChange={this.handleAccessFilterChange}
        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
      />
    );
    const mobileAccessFilter = (
      <AccessFilter
        id="SearchPageWithMap.mobileAccessFilter"
        value={accessFilterValue}
        onChange={this.handleAccessFilterChange}
        variant="mobilePills"
      />
    );
    const hasCategoryPrimaryFilter = availablePrimaryFilters.some(
      filterConfig => filterConfig.key === 'categoryLevel'
    );

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        <TopbarContainer
          rootClassName={topbarClasses}
          currentPage="SearchPage"
          currentSearchParams={urlQueryParams}
        />

        <div
          className={classNames(css.container, {
            [css.containerGridMode]: isGridView,
          })}
          data-testid="searchPageContainer"
        >
          {!isMobileLayout && (
            <div className={css.searchPageFilters}>
              <div className={css.middleFilters}>
                <SearchFiltersPrimary isSearchPageFilter={true} {...propsForSecondaryFiltersToggle}>
                  {availablePrimaryFilters.map(config => {
                    return (
                      <React.Fragment key={`SearchFiltersPrimary.${config.key}`}>
                        <FilterComponent
                          idPrefix="SearchFiltersPrimary"
                          className={css.toolbarFilterControl}
                          config={config}
                          marketplaceCurrency={marketplaceCurrency}
                          urlQueryParams={validQueryParams}
                          initialValues={initialValues(this.props, this.state.currentQueryParams)}
                          getHandleChangedValueFn={this.getHandleChangedValueFn}
                          intl={intl}
                          showAsPopup
                          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                        />
                        {config.key === 'categoryLevel' ? desktopAccessFilter : null}
                      </React.Fragment>
                    );
                  })}
                  {hasCategoryPrimaryFilter ? null : desktopAccessFilter}
                  {sortConfig.active ? sortBy('desktop') : null}
                </SearchFiltersPrimary>
              </div>
              <div className={css.searchControls}>
                <SectionTopbarFilter
                  isSearchPageFilter={true}
                  intl={intl}
                  viewport={viewport}
                  history={history}
                />
                <button
                  className={css.toggleMapButton}
                  onClick={this.onToggleMap}
                  type="button"
                  data-testid="search-view-toggle"
                  aria-label={isGridView ? 'Switch to map view' : 'Switch to grid view'}
                >
                  {isGridView ? <Icons name="map" /> : <Icons name="grid" />}
                </button>
              </div>
            </div>
          )}
          <div
            className={classNames(css.searchMainContainer, {
              [css.searchMainContainerGridMode]: isGridView,
            })}
            data-testid="search-main-container"
            data-view-mode={viewMode}
          >
            <div className={css.searchResultContainer}>
              {/* <SectionTopbarFilter
              isSearchPageFilter={true}
              intl={intl}
              viewport={viewport}
              history={history}
            /> */}
              <SearchFiltersMobile
                className={css.searchFiltersMobileMap}
                urlQueryParams={validQueryParams}
                publicMooragesToggleComponent={mobileAccessFilter}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onMapIconClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
                onListIconClick={() => this.setState({ isSearchMapOpenOnMobile: false })}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                noResultsInfo={noResultsInfo}
                isMapVariant
                mobileViewMode={this.state.isSearchMapOpenOnMobile ? 'map' : 'list'}
              >
                {availableFilters.map(config => {
                  return (
                    <FilterComponent
                      key={`SearchFiltersMobile.${config.key}`}
                      idPrefix="SearchFiltersMobile"
                      config={config}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile>
              <MainPanelHeader
                className={css.mainPanelMapVariant}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
                onToggleMap={this.onToggleMap}
                isMapToggled={isGridView}
              >
                <div className={css.searchOptions}>
                  <div className={css.searchResultSummary}>
                    <span className={css.resultsFound}>
                      {!searchInProgress ? (
                        <div className={css.totalCount}>
                          <FormattedMessage
                            id="MainPanelHeader.foundResults"
                            values={{ count: totalItems }}
                          />
                        </div>
                      ) : null}
                    </span>
                  </div>
                </div>
              </MainPanelHeader>
              {isSecondaryFiltersOpen ? (
                <div className={classNames(css.searchFiltersPanel)}>
                  <SearchFiltersSecondary
                    urlQueryParams={validQueryParams}
                    listingsAreLoaded={listingsAreLoaded}
                    applyFilters={this.applyFilters}
                    cancelFilters={this.cancelFilters}
                    resetAll={this.resetAll}
                    onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
                  >
                    {customSecondaryFilters.map(config => {
                      return (
                        <FilterComponent
                          key={`SearchFiltersSecondary.${config.key}`}
                          idPrefix="SearchFiltersSecondary"
                          config={config}
                          marketplaceCurrency={marketplaceCurrency}
                          urlQueryParams={validQueryParams}
                          initialValues={initialValues(this.props, this.state.currentQueryParams)}
                          getHandleChangedValueFn={this.getHandleChangedValueFn}
                          intl={intl}
                          showAsPopup={false}
                        />
                      );
                    })}
                  </SearchFiltersSecondary>
                </div>
              ) : (
                <div className={css.listingsForMapVariant}>
                  {searchListingsError ? (
                    <H3 className={css.error}>
                      <FormattedMessage id="SearchPage.searchError" />
                    </H3>
                  ) : null}
                  {!isValidDatesFilter ? (
                    <H5>
                      <FormattedMessage id="SearchPage.invalidDatesFilter" />
                    </H5>
                  ) : null}
                  <SearchResultsPanel
                    className={classNames(css.searchListingsPanel, {
                      [css.newSearchInProgress]: searchInProgress && !searchListingsError,
                    })}
                    listings={synchronizedListings}
                    pagination={listingsAreLoaded ? pagination : null}
                    search={parse(location.search)}
                    setActiveListing={onActivateListing}
                    isMapVariant
                    isSearchPageFilter={isGridView}
                    onPublicMoorageCheckInSuccess={this.handlePublicMoorageCheckInSuccess}
                  />
                  {searchInProgress && !searchListingsError ? (
                    <div className={css.searchLoadingOverlay}>
                      <SearchLoadingIndicator />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            {isGridView ? null : (
              <ModalInMobile
                className={css.mapPanel}
                id="SearchPage.map"
                isModalOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                isSearchMapModal={true}
              >
                <div className={css.mapWrapper} data-testid="searchMapContainer">
                  {shouldShowSearchMap && !isGridView ? (
                    <SearchMap
                      reusableContainerClassName={css.map}
                      activeListingId={activeListingId}
                      bounds={boundsForSearchMap}
                      center={origin}
                      isSearchMapOpenOnMobile={this.state.isSearchMapOpenOnMobile}
                      location={location}
                      listings={synchronizedListings || []}
                      onActivateListing={onActivateListing}
                      onMapMoveEnd={this.onMapMoveEnd}
                      onCloseAsModal={() => {
                        onManageDisableScrolling('SearchPage.map', false);
                      }}
                      messages={intl.messages}
                      onFetchListings={onFetchListings}
                    />
                  ) : null}
                </div>
              </ModalInMobile>
            )}
          </div>

          <Modal
            id="SearchPageWithMap.notifyMe"
            isOpen={notifyModalOpen}
            onClose={this.closeNotifyModal}
            usePortal
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <FinalForm
              onSubmit={this.handleNotifySubmit}
              initialValues={{
                email: currentUser?.attributes?.email || '',
                homePort: '',
                company_website: '',
              }}
              render={({ handleSubmit, values }) => {
                const emailRequired = validators.required(
                  intl.formatMessage({ id: 'SearchPageWithMap.Modal.emailRequired' })
                );
                const emailValid = validators.emailFormatValid(
                  intl.formatMessage({ id: 'SearchPageWithMap.Modal.emailInvalid' })
                );

                return (
                  <Form className={css.comingSoonModal} onSubmit={handleSubmit}>
                    <Field
                      component="input"
                      type="text"
                      id="company_website"
                      name="company_website"
                      autoComplete="off"
                      tabIndex={-1}
                      aria-hidden="true"
                      className={css.honeypotField}
                    />
                    <div className={css.comingSoonHead}>
                      <Heading as="h4">
                        {intl.formatMessage({ id: 'SearchPageWithMap.Modal.heading' })}
                      </Heading>
                      <p>{intl.formatMessage({ id: 'SearchPageWithMap.Modal.description1' })}</p>
                      <p>{intl.formatMessage({ id: 'SearchPageWithMap.Modal.description2' })}</p>
                      <p>{intl.formatMessage({ id: 'SearchPageWithMap.Modal.description3' })}</p>
                    </div>
                    <div className={css.comingSoonEmail}>
                      {/* Email Field */}
                      <FieldTextInput
                        type="email"
                        id="email"
                        name="email"
                        autoComplete="email"
                        label={intl.formatMessage({
                          id: 'SearchPageWithMap.Modal.emailLabel',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'SearchPageWithMap.Modal.emailPlaceholder',
                        })}
                        validate={validators.composeValidators(emailRequired, emailValid)}
                      />
                    </div>
                    <div className={css.comingSoonEmail}>
                      {/* Home port / cruising area Field */}
                      <FieldTextInput
                        type="text"
                        id="homePort"
                        name="homePort"
                        autoComplete="off"
                        label={intl.formatMessage({
                          id: 'SearchPageWithMap.Modal.homePortLabel',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'SearchPageWithMap.Modal.homePortPlaceholder',
                        })}
                      />
                    </div>
                    <div className={css.checkbox}>
                      {/* Checkbox using FieldCheckbox */}
                      <FieldCheckbox
                        id="isCheckboxChecked"
                        className={css.deliveryCheckbox}
                        name="isCheckboxChecked"
                        label={intl.formatMessage({
                          id: 'SearchPageWithMap.Modal.checkboxText',
                        })}
                      />
                    </div>
                    {/* Submit Button */}
                    <Button type="submit" disabled={addContactLoading || !values.email}>
                      {addContactLoading
                        ? intl.formatMessage({ id: 'SearchPageWithMap.Modal.notifyMeBtn1' })
                        : intl.formatMessage({ id: 'SearchPageWithMap.Modal.notifyMeBtn2' })}
                    </Button>
                  </Form>
                );
              }}
            />
          </Modal>
        </div>
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  activeListingId: null,
  addContactLoading: false,
};

SearchPageComponent.propTypes = {
  listings: array,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onSubmitOnBuoyCrmRecord: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  } = state.SearchPage;

  const { addContactLoading, addContactError } = state.global;

  const listings = getListingsById(state, currentPageResultIds);

  const { currentUser } = state.user;

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    addContactLoading,
    addContactError,
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
  onFetchListings: params => dispatch(fetchListings(params)),
  onSubmitOnBuoyCrmRecord: body => dispatch(submitOnBuoyCrmRecord(body)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  withViewport,
  connect(mapStateToProps, mapDispatchToProps)
)(EnhancedSearchPage);

export default SearchPage;
